// The grid while it is edited: each price a Dinero (or null, not offered for the term), turned to
// and from the contract's Money string only by parseMoney and formatMoney (docs/screaming.md →
// The upsert trap). The contract still checks the result before it is sent.
import { formatMoney, parseMoney, type Dinero } from "@kilat/contracts/money";
import { emptyCosts, type TldCost } from "@kilat/contracts/tld-pricing";

export const PRICE_FIELDS = ["register", "transfer", "renew"] as const;
export type PriceField = (typeof PRICE_FIELDS)[number];

/** One term of the grid while it is edited. */
export type CostRow = { years: number; enabled: boolean } & Record<PriceField, Dinero<number> | null>;

const toAmount = (money: string | null) => (money === null ? null : parseMoney(money));
const toMoney = (amount: Dinero<number> | null) => (amount === null ? null : formatMoney(amount));

/** The grid for a TLD: every term, its saved costs where it has them. */
export function costRows(saved: readonly TldCost[]): CostRow[] {
  return emptyCosts().map((empty) => {
    const row = saved.find((cost) => cost.years === empty.years) ?? empty;
    return {
      years: row.years,
      enabled: row.enabled,
      register: toAmount(row.register),
      transfer: toAmount(row.transfer),
      renew: toAmount(row.renew),
    };
  });
}

/** The grid as the request body takes it, still to be checked against the contract. */
export const toCosts = (rows: readonly CostRow[]) =>
  rows.map((row) => ({
    years: row.years,
    enabled: row.enabled,
    register: toMoney(row.register),
    transfer: toMoney(row.transfer),
    renew: toMoney(row.renew),
  }));
